export const DEFAULT_TIMEOUT = 300000; // 5 minutes
// export const DEFAULT_TIMEOUT = 30000; // 30 seconds

export const MICRO_SERVICE_ROUTES = {
	NOTIFICATIONS: "/notifications",
	USERS: "/users",
	INSTITUTES: "/institutes",
};
export const API_ROUTES = {
	AUTH: `${MICRO_SERVICE_ROUTES.USERS}/auth/v1`,
	USERS: `${MICRO_SERVICE_ROUTES.USERS}/users/v1`,
	NOTIFICATIONS: `${MICRO_SERVICE_ROUTES.NOTIFICATIONS}/notifications`,
	DASHBOARD: `${MICRO_SERVICE_ROUTES.INSTITUTES}/students/assessment-body`,
	DATASHEETS: `${MICRO_SERVICE_ROUTES.INSTITUTES}/students/assessment`,
	ATTACHMENTS: `${MICRO_SERVICE_ROUTES.INSTITUTES}/attachments`,
	ATTACHMENT_BY_KEY: (key: string) => `${MICRO_SERVICE_ROUTES.INSTITUTES}/attachments/${key}`,
} as const;

export const API_PATHS = {
	AUTH: {
		SIGN_IN: "/login",
		SIGN_UP: "/email/register",
		VERIFY_EMAIL: "/verify-email",
		FORGOT_PASSWORD: "/forgot-password",
		RESET_PASSWORD: "/reset-password",
		VERIFY_OTP: "/verify-otp",
		RESEND_OTP: "/resend-otp",
		SET_PASSWORD: "/set-password",
	},
	USER: {
		GET_PROFILE: "/profile",
		PROFILE_IMAGE_UPLOAD_URL: "/profile/image/upload-url",
		PROFILE_IMAGE_UPDATE: "/profile/image",
	},

	NOTIFICATIONS: {
		READ: "/read",
		STREAM: "/user/{id}/stream",
	},

	DATASHEETS: {
		DATASHEETS: "/datesheets",
		UPLOAD_RESULT: "/upload-result",
		UPLOAD_DATASHEET: "/upload-datesheet",
	},
	EXAM_RESULTS: {
		RESULTS: "/results",
	},
	INSTITUTES: {
		/** GET /institutes/city — paginated cities (provinceId, search) */
		CITIES: "/institutes/city",
		/** GET /institutes/religion — paginated religions */
		RELIGION: "/institutes/religion",
		/** GET /institutes/gender — paginated genders */
		GENDER: "/institutes/gender",
		/** GET /institutes/course-stream — course streams (status filter) */
		COURSE_STREAM: "/institutes/course-stream",
		/** GET /institutes/{id}/institute-courses — courses for an institute (assessment / affiliated lists). */
		INSTITUTE_COURSES: (instituteId: string | number) => `/institutes/${instituteId}/institute-courses`,
		WITH_BATCHES: "/institutes/institutes/with-batches",
		COURSES_WITH_BATCHES: (instituteId: string) => `/institutes/institutes/${instituteId}/courses-with-batches`,
		BATCHES: (instituteId: string) => `/institutes/institutes/${instituteId}/batches`,
		TRADE_SESSION: "/institutes/trade-session",
	},
	SYSTEM: {
		APPLICATION_SESSIONS: "/institutes/system/filters/application-sessions",
	},
	ATTACHMENTS: {
		UPLOAD: "/institutes/attachments/upload",
		DOWNLOAD_ZIP: "/institutes/attachments/download-zip",
	},
};

export const SERVER_ROUTES = {
	AUTH: {
		SIGN_IN: API_ROUTES?.AUTH + API_PATHS?.AUTH?.SIGN_IN,
		SIGN_UP: API_ROUTES?.AUTH + API_PATHS?.AUTH?.SIGN_UP,
		VERIFY_EMAIL: API_ROUTES.AUTH + API_PATHS.AUTH.VERIFY_EMAIL,
		FORGOT_PASSWORD: API_ROUTES.AUTH + API_PATHS.AUTH.FORGOT_PASSWORD,
		RESET_PASSWORD: API_ROUTES.AUTH + API_PATHS.AUTH.RESET_PASSWORD,
		VERIFY_OTP: API_ROUTES.AUTH + API_PATHS.AUTH.VERIFY_OTP,
		RESEND_OTP: API_ROUTES.AUTH + API_PATHS.AUTH.RESEND_OTP,
		SET_PASSWORD: API_ROUTES.AUTH + API_PATHS.AUTH.SET_PASSWORD,
	},
	USER: {
		GET_PROFILE: API_ROUTES.AUTH + API_PATHS.USER.GET_PROFILE,
		PROFILE_IMAGE_UPLOAD_URL: API_ROUTES.USERS + API_PATHS.USER.PROFILE_IMAGE_UPLOAD_URL,
		PROFILE_IMAGE_UPDATE: API_ROUTES.USERS + API_PATHS.USER.PROFILE_IMAGE_UPDATE,
		PROFILE_UPDATE: API_ROUTES.USERS + API_PATHS.USER.GET_PROFILE,
	},

	NOTIFICATIONS: {
		NOTIFICATIONS: API_ROUTES.NOTIFICATIONS,
		READ: (id: string) => `${API_ROUTES.NOTIFICATIONS}/${id}${API_PATHS.NOTIFICATIONS.READ}`,
		STREAM: (id: string) => `${API_ROUTES.NOTIFICATIONS}${API_PATHS.NOTIFICATIONS.STREAM.replace("{id}", id)}`,
	},

	DASHBOARD: {
		/** GET /institutes/assessment-body/dashboard/stats — single payload for KPI cards + stat boxes */
		STATS: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/stats`,
		/** GET /institutes/assessment-body/dashboard/offered-courses */
		OFFERED_COURSES: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/offered-courses`,
		/** GET /institutes/assessment-body/dashboard/affiliated-institutes */
		AFFILIATED_INSTITUTES: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/affiliated-institutes`,
		/** GET /institutes/assessment-body/dashboard/platform-registered-institutes */
		PLATFORM_REGISTERED_INSTITUTES: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/platform-registered-institutes`,
		/** GET /institutes/assessment-body/dashboard/registered-students */
		REGISTERED_STUDENTS: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/registered-students`,
		/** GET /institutes/assessment-body/dashboard/pass-out-students */
		PASS_OUT_STUDENTS: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/pass-out-students`,
		/** GET /institutes/assessment-body/dashboard/pbte-term-exam-results */
		PBTE_TERM_EXAM_RESULTS: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/pbte-term-exam-results`,
		/** GET /institutes/assessment-body/dashboard/pbte-term-exam-results/students/{studentUuid} */
		PBTE_STUDENT_TERM_EXAM_RESULTS: (studentUuid: string) =>
			`${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/pbte-term-exam-results/students/${studentUuid}`,
		/** GET /institutes/assessment-body/dashboard/pbte-affiliated-courses-by-institute */
		PBTE_AFFILIATED_COURSES_BY_INSTITUTE: `${MICRO_SERVICE_ROUTES.INSTITUTES}/assessment-body/dashboard/pbte-affiliated-courses-by-institute`,
	},
	DATASHEETS: {
		DATASHEETS: API_ROUTES.DATASHEETS + API_PATHS.DATASHEETS.DATASHEETS,
		UPLOAD_RESULT: API_ROUTES.DATASHEETS + API_PATHS.DATASHEETS.UPLOAD_RESULT,
		UPLOAD_DATASHEET: API_ROUTES.DATASHEETS + API_PATHS.DATASHEETS.UPLOAD_DATASHEET,
	},
	EXAM_RESULTS: {
		RESULTS: API_ROUTES.DATASHEETS + API_PATHS.EXAM_RESULTS.RESULTS,
	},
	INSTITUTES: {
		CITIES: API_PATHS.INSTITUTES.CITIES,
		RELIGION: API_PATHS.INSTITUTES.RELIGION,
		GENDER: API_PATHS.INSTITUTES.GENDER,
		COURSE_STREAM: API_PATHS.INSTITUTES.COURSE_STREAM,
		INSTITUTE_COURSES: (instituteId: string | number) => API_PATHS.INSTITUTES.INSTITUTE_COURSES(instituteId),
		WITH_BATCHES: API_PATHS.INSTITUTES.WITH_BATCHES,
		COURSES_WITH_BATCHES: (instituteId: string) => API_PATHS.INSTITUTES.COURSES_WITH_BATCHES(instituteId),
		BATCHES: (instituteId: string) => API_PATHS.INSTITUTES.BATCHES(instituteId),
		TRADE_SESSION: API_PATHS.INSTITUTES.TRADE_SESSION,
	},
	SYSTEM: {
		APPLICATION_SESSIONS: API_PATHS.SYSTEM.APPLICATION_SESSIONS,
	},
	ATTACHMENTS: {
		UPLOAD: API_PATHS.ATTACHMENTS.UPLOAD,
		DOWNLOAD_ZIP: API_PATHS.ATTACHMENTS.DOWNLOAD_ZIP,
	},
} as const;

export const QUERY_KEYS = {
	USER: {
		PROFILE_IMAGE_UPDATE: "profile-image-update",
		PROFILE_UPDATE: "profile-update",
		GET_PROFILE: "profile",
	},
	ATTACHMENTS: {
		PRESIGNED_URL: "presigned-url",
		ATTACHMENT_BY_KEY: "attachment-by-key",
	},
	DASHBOARD: {
		STATS: "dashboard-stats",
		UPCOMING_EXAMS: "upcoming-exams",
		RECENT_EXAM_RESULTS: "recent-exam-results",
		OFFERED_COURSES: "dashboard-offered-courses",
		AFFILIATED_INSTITUTES: "dashboard-affiliated-institutes",
		PLATFORM_REGISTERED_INSTITUTES: "dashboard-platform-registered-institutes",
		CITIES_INFINITE: "dashboard-cities-infinite",
		RELIGIONS: "dashboard-religions",
		GENDERS: "dashboard-genders",
		COURSE_STREAMS: "dashboard-course-streams",
		REGISTERED_STUDENTS: "dashboard-registered-students",
		PASS_OUT_STUDENTS: "dashboard-pass-out-students",
		PBTE_TERM_EXAM_RESULTS: "dashboard-pbte-term-exam-results",
		PBTE_STUDENT_TERM_EXAM_RESULTS: "dashboard-pbte-student-term-exam-results",
		PBTE_AFFILIATED_COURSES_BY_INSTITUTE: "dashboard-pbte-affiliated-courses-by-institute",
		APPLICATION_SESSIONS_SELECT: "dashboard-application-sessions",
		INSTITUTE_COURSES: "dashboard-institute-courses",
	},
};
export const QUERY_PARAMS = {};

export const AUTH_ENDPOINTS = [
	SERVER_ROUTES.AUTH.SIGN_IN,
	SERVER_ROUTES.AUTH.SIGN_UP,
	SERVER_ROUTES.AUTH.FORGOT_PASSWORD,
	SERVER_ROUTES.AUTH.RESET_PASSWORD,
	SERVER_ROUTES.AUTH.VERIFY_OTP,
	SERVER_ROUTES.AUTH.RESEND_OTP,
	SERVER_ROUTES.AUTH.VERIFY_EMAIL,
	SERVER_ROUTES.AUTH.SET_PASSWORD,
];
